import React, { useEffect, useMemo, useState } from 'react';
import './InkyPenSeries.scss';
import { SeriesPictureAndBreadCrumb, SeriesTitleAndDescription } from './Header/Header';
import SeriesInfo from './ComicInfo/SeriesInfo';
import DesktopRating from './Desktop/DesktopRating/DesktopRating';
import { InkyPenNavbar } from '../../InkyPenNavbar/InkyPenNavbar';
import BrowseManga from './BrowseManga/BrowseManga';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { InkyPenFooter } from '../../InkyPenFooter/InkyPenFooter';
import InkyPenSubscription from '../../InkyPenSubscription/InkyPenSubscription';
import InkyPenAvailable from '../../InkyPenUIKit/InkyPenAvailable/InkyPenAvailable';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { InkyPenNotFound } from '../../InkyPenNotFound/InkyPenNotFound';
import Log from '../../Services/Logger';
import { ReactComponent as Heart } from '../../Assets/inky-icons/Heart_InkyPen.svg';


import {
    FetchSingleSeriesAtom,
    LoginStatusForSeriesAtom,
    SingleSeriesAtom,
    SingleSeriesErrorAtom,
} from '../../../store/SeriesStore';
import { RenderChapterToRead } from './RenderChapterToRead';
import { LoginStatus, LoginStatusAtom } from '../../../store/AccountStore';
import InkyPenBreadCrumb from '../../InkyPenUIKit/InkyPenBreadCrumb/InkyPenBreadCrumb';
import InkyPenArrow from '../../InkyPenUIKit/InkyPenArrow/InkyPenArrow';
import { Information } from './Information/information';
import EntireCatalogue from '../../EntireCatalogue/EntireCatalogue';
import PreFooter from '../../InkyPenFooter/PreFooter';
import { useIsSubscribed } from '../../Hooks/AuthUtils';
import { SeriesGeoRestricted } from '../../InkypenGeoRestricted/InkyPenGeoRestricted';
import SeriesPoster from '../../InkyPenUIKit/SeriesPoster/SeriesPoster';
import { SeriesType } from './InkypenSeries.constants';

interface InkypenSeriesParams {
    sid: string;
}

export function InkyPenSeries(): JSX.Element {
    const { sid } = useParams<InkypenSeriesParams>();
    const error = useAtomValue(SingleSeriesErrorAtom);
    const [loading, setLoading] = useState(true);
    const [finalSeries, fetchSeries] = useAtom(FetchSingleSeriesAtom);
    const [lastLoginStatus, setLastLoginStatus] = useAtom(LoginStatusForSeriesAtom);
    const loginStatus = useAtomValue(LoginStatusAtom);
    const history = useHistory();
    const series = useAtomValue(SingleSeriesAtom);
    const isUserSubscribed = useIsSubscribed();

    useEffect(() => {
        Log.debug(`Series trigger: sid ${sid}, loginStatus: ${loginStatus}`);
        if (loginStatus === 'LoggedIn' || loginStatus === 'LoggedOut') {
            //Ensure that we only load if the login status has actually changed.
            if (finalSeries?.id.toString() !== sid && finalSeries?.readableUrl !== sid || loginStatus !== lastLoginStatus) {
                Log.info('Loading series...');
                setTimeout(() => {
                    window.scrollTo(0, 0);
                });
                fetchSeries(sid);
            }
        }
    }, [sid, loginStatus]);

    useEffect(() => {
        if (finalSeries !== null) {
            ReactGA.send({ hitType: 'pageview', page: window.location.href, title: 'Series | ' + finalSeries.title });
            setLoading(false);
        }
    }, [finalSeries]);

    useEffect(() => {
        if (finalSeries?.readableUrl && finalSeries.id.toString() === sid) {
            history.replace('/series/' + finalSeries.readableUrl);
        }
    }, [finalSeries, sid]);


    Log.info('Render Series...');

    function SeriesHelmet() {

        Log.info('Render Series Helmet...');
        return (
            <Helmet>
                {/*Basic SEO Tags*/}
                <title>{series?.title ?? 'InkyPen | Series'}</title>
                <meta name='description' content={series?.description} />
                {series?.readableUrl && (
                    <link rel='canonical'
                          href={process.env.REACT_APP_WEBSITE_ROOT + 'series/' + series.readableUrl + '/'} />
                )}

                {/*OpenGraph*/}
                <meta property='og:title' content={series?.title} />
                <meta property='og:type' content='book' />
                <meta property='og:description' content={series?.description} />
                <meta property='og:locale' content='en_GB' />
                <meta property='og:image' content={series?.thumbnails[0]?.url} />

                {/*Twitter*/}
                <meta property='twitter:card' content='summary' />
                {/*<meta property='twitter:site' content='@InkypenManga' />*/}
                <meta property='twitter:title' content={series?.title} />
                <meta property='twitter:description' content={series?.description} />
                <meta property='twitter:image:src' content={series?.thumbnails[0]?.url} />
                <meta property='twitter:image:alt' content={'Image from ' + series?.title} />
            </Helmet>
        );
    }

    function RenderBreadCrumb() {
        const type = SeriesType[series.type]?.toUpperCase();

        return <InkyPenBreadCrumb firstNoPadding
                                  items={['BROWSE', type, 'SERIES:']}
                                  highlightLast={true}
                                  links={[undefined, undefined, undefined]}
                                  styles='series-desktop-header-breadcrumb-styles' />;
    }

    if (error === 'NotFound') {
        return (<InkyPenNotFound />);
    }

    if (error === 'GeoRestricted') {
        return (<SeriesGeoRestricted />);
    }

    if (finalSeries?.id.toString() !== sid && finalSeries?.readableUrl !== sid) {
        return <Helmet>
            <title>{'InkyPen | Series'}</title>
        </Helmet>;
    }

    if (loading) {
        return <main className='series-container-loading'>
            <div className='series-max-wrapper'>
                <InkyPenNavbar />
                <div className='series-grid-header'>
                    <SeriesPoster preloadImage={true} hover={false} src={null} />
                    <div className='series-desktop-header-info-wrapper-series'></div>
                </div>
            </div>
        </main>;
    }

    return (
        <>
            <SeriesHelmet />
            <main className='series-container'>
                <div className='series-max-wrapper'>
                    <InkyPenNavbar />

                    <div className='inky-series-max-width'>
                        <div className='header-on-mobile'>

                            <div className={'breadcrumb-follow-wrapper'}>
                                <RenderBreadCrumb />

                                {/*<div className='icon-follow-wrapper'>*/}
                                {/*    <Heart className='icon-follow'/>*/}
                                {/*    <span className='icon-text'>Follow</span>*/}
                                {/*</div>*/}
                            </div>

                            <div className='inky-series-title-mobile'>
                                <SeriesTitleAndDescription />
                            </div>

                            <div className='series-grid-header'>
                                <SeriesPictureAndBreadCrumb />
                                <div className='series-desktop-header-info-wrapper-series'>
                                    <div className='inky-series-title-desktop'>
                                        <SeriesTitleAndDescription />
                                    </div>

                                    {/*{isUserSubscribed && series.products?.length > 0 &&*/}
                                    {/*    <div className='start-reading-wrapper'>*/}
                                    {/*        <Link to={'/reader/' + series?.products[0]?.id} className='start-reading'>*/}
                                    {/*            Start Reading*/}
                                    {/*        </Link>*/}
                                    {/*    </div>}*/}

                                    {/*<div className='series-desktop-reader-chapter'>*/}
                                    {/*    <RenderChapterToRead />*/}
                                    {/*</div>*/}
                                </div>
                            </div>
                        </div>

                        <div className='inky-series-title-desktop'>
                            <Information />
                        </div>

                        {/*<div className='read-chapters-in-series-on-mobile'>*/}
                        {/*    <RenderChapterToRead />*/}
                        {/*</div>*/}
                    </div>
                </div>
                {/*<SimulPub />*/}
                {/* TODO: Post Beta Feature */}
                {/*hidee*/}
                {/*<Awards />*/}
                {/*hidee*/}
                <div className='series-max-wrapper'>
                    <SeriesInfo />
                    {/*hidee*/}

                    <div className='inkypen-available-on-mobile'>
                        <InkyPenAvailable items={[]} />
                    </div>

                    {/*<BrowseManga />*/}

                    <div className='steam-container-series'>
                        <span className='available-text'>Available on</span>
                        <h3 className='steam'>Nintendo Switch&trade; and soon STEAM®</h3>

                        <div className='steam-links-wrapper'>
                            <Link to={{ pathname: 'https://store.steampowered.com/app/681220/InkyPen/' }}
                                  target={'_blank'} className='steam-link'>STEAM Store Page</Link>
                            <Link to={{ pathname: 'https://www.nintendo.com/us/store/products/inkypen-switch/' }}
                                  target={'_blank'}
                                  className='steam-link nintendo-link'>Nintendo eShop</Link>
                        </div>
                    </div>

                </div>
            </main>
            {/*<InkyPenSubscription />*/}
            <InkyPenFooter />
        </>
    );
};

export default InkyPenSeries;